import React, { useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';

import { useCollection, useDocument } from '@nandorojo/swr-firestore';
import { BoxTitle } from '@pages/MainHome/common/commonStyle';
import { CollectionPath, ImainhomeDoc } from '@typings/collections.enum';
import { ImainHome } from '@typings/setting/mainHome.interface';
import { replaceContents } from '@utils/replaceContents';
import { IguestbookItem } from '@typings/guestbook/guestbook.interface';
import { Link, useNavigate } from 'react-router-dom';
import TitleWidgetSec from '@pages/MainHome/components/TitleWidgetSec';
import { MAIN_HOME_SEC_TYPE } from '@typings/setting/MainHomeSecType';
import { IcommentItem } from '@typings/common/comment.interface';
import { scrollbarStyle } from '@styles/variants/scrollbar';
import TimeAgo from '@components/dataDisplay/TimeAgo';
import { ACCESS_TYPE } from '@typings/common/accessType';
import CommentQuill, {
  initialToolbar,
} from '@components/library/Quill/CommentQuillLazy';
import { useRecoilValue } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import { updateGuestbook } from '@store/firebase/guestBook/update-guestbook';

const GuestbookGrid = styled.div`
  display: grid;
  grid-template-rows: 1fr auto;
  height: 100%;
  overflow: hidden;
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  padding: 12px;
  height: 100%;
  ${scrollbarStyle}
`;

const MessageWrapper = styled.div<{ $isComment: boolean }>`
  display: flex;
  justify-content: ${({ $isComment }) =>
    $isComment ? 'flex-end' : 'flex-start'};
  align-items: flex-start;
  gap: 8px;
`;

const MessageBubble = styled(Link)<{ $isComment: boolean }>`
  max-width: 90%;
  padding: 10px 14px;
  border-radius: ${({ $isComment }) =>
    $isComment ? '12px 12px 2px 12px' : '12px 12px 12px 2px'};
  background: ${({ $isComment }) =>
    $isComment ? 'var(--primaryColor)' : 'var(--cardBackgroundColor)'};
  color: ${({ $isComment }) => ($isComment ? 'white' : 'var(--textColor)')};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  word-break: break-word;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  p {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: inherit;

    /* 2줄 말줄임 */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  img {
    max-width: 100%;
    border-radius: 6px;
    margin-top: 6px;
  }
`;

const TimeStamp = styled.div<{ $isComment: boolean }>`
  font-size: 10px;
  color: var(--subTextColor);
  margin-top: 4px;
  text-align: ${({ $isComment }) => ($isComment ? 'right' : 'left')};
  padding: 0 4px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--subTextColor);
  font-size: 14px;
`;

const WriteSection = styled.div`
  background: var(--cardBackgroundColor);
  border-top: 2px solid var(--lineColor);
  padding: 12px;
`;

const WriteButton = styled.button`
  width: 100%;
  padding: 12px;
  background: var(--cardBackgroundColor);
  border-radius: var(--cardBorderRadius);
  color: var(--textColor);
  font-size: 14px;
  text-align: left;
  cursor: pointer;
`;

type Props = {};
function MiniGuestbook({}: Props) {
  const navigate = useNavigate();
  const currentUid = useRecoilValue(currentUserIDState);
  const [isWriting, setIsWriting] = useState(false);
  const [contents, setContents] = useState('');

  const { data: mainHomeData } = useDocument<ImainHome>(
    `${CollectionPath.MAINHOME}/${ImainhomeDoc.MAINHOME}`,
  );

  // 연동 게시판 목록 (기본값: guestbook)
  const integrateBoards =
    mainHomeData?.guestbookIntegrate &&
    mainHomeData.guestbookIntegrate.length > 0
      ? mainHomeData.guestbookIntegrate
      : ['guestbook'];
  const mainBoard = integrateBoards[0] || 'guestbook';

  const { data: guestbookList } = useCollection<IguestbookItem>(mainBoard, {
    listen: true,
    orderBy: ['createdAt', 'desc'],
    limit: mainHomeData?.guestbookLimit || 20,
  });

  // 게시글별 댓글 가져오기
  const { data: allComments } = useCollection<IcommentItem>(
    mainBoard ? `${mainBoard}Comment` : null,
    {
      listen: true,
      orderBy: ['createdAt', 'asc'],
    },
  );

  // 채팅 스타일로 게시글-댓글을 펼쳐서 표시
  const chatMessages = useMemo(() => {
    const messages: Array<{
      id: string;
      content: string;
      createdAt: any;
      isComment: boolean;
      linkTo: string;
      boardId?: string;
    }> = [];

    // accessType이 PUBLIC인 게시글만 필터링
    const publicGuestbooks =
      guestbookList?.filter(
        (guestbook) => guestbook.accessType === ACCESS_TYPE.PUBLIC,
      ) || [];

    publicGuestbooks.forEach((guestbook) => {
      // 게시글 (왼쪽)
      messages.push({
        id: guestbook.id || '',
        content: guestbook.contents || '',
        createdAt: guestbook.createdAt,
        isComment: false,
        linkTo: `${mainBoard}/${guestbook.id}`, // 동적 경로 사용
        boardId: guestbook.id,
      });

      // 해당 게시글의 댓글들 (오른쪽)
      const comments =
        allComments?.filter((comment) => comment.boardId === guestbook.id) ||
        [];

      comments.forEach((comment) => {
        messages.push({
          id: comment.id || '',
          content: comment.contents || '',
          createdAt: comment.createdAt,
          isComment: true, // 댓글은 오른쪽
          linkTo: `${mainBoard}/${guestbook.id}#${comment.id}`, // 동적 경로 사용
          boardId: guestbook.id,
        });
      });
    });

    return messages;
  }, [guestbookList, allComments, mainBoard]);

  // 게시글 작성
  const handleSubmit = useCallback(async () => {
    if (!contents.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }

    try {
      await updateGuestbook({
        collectionPath: mainBoard,
        data: {
          contents,
          accessType: ACCESS_TYPE.PUBLIC,
          authorUid: currentUid || '',
        },
      });
      setContents('');
      setIsWriting(false);
      alert('방명록이 작성되었습니다!');
    } catch (error) {
      console.error('방명록 작성 오류:', error);
      alert('작성 중 오류가 발생했습니다.');
    }
  }, [contents, currentUid, mainBoard]);

  return (
    <TitleWidgetSec
      titleDesignType={mainHomeData?.titleDesignType}
      title={mainHomeData?.guestbookTitle || '미니 방명록'}
      style={{
        gridArea: MAIN_HOME_SEC_TYPE.GUESTBOOK,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <GuestbookGrid>
        {/* 상단: 스크롤 가능한 채팅 목록 */}
        <ChatContainer>
          {chatMessages.length > 0 ? (
            chatMessages.map((message, index) => (
              <div key={`${message.id}-${index}`}>
                <MessageWrapper $isComment={message.isComment}>
                  <MessageBubble
                    to={message.linkTo}
                    $isComment={message.isComment}
                  >
                    <p
                      dangerouslySetInnerHTML={{
                        __html: replaceContents(message.content),
                      }}
                    />
                  </MessageBubble>
                </MessageWrapper>
                <TimeStamp $isComment={message.isComment}>
                  <TimeAgo createdAt={message.createdAt} />
                </TimeStamp>
              </div>
            ))
          ) : (
            <EmptyState>작성된 방명록이 없습니다</EmptyState>
          )}
        </ChatContainer>

        {/* 하단: 고정된 작성 섹션 (로그인 사용자에게만) */}
        {currentUid && (
          <WriteSection>
            {!isWriting ? (
              <WriteButton onClick={() => setIsWriting(true)}>
                글 남기기...
              </WriteButton>
            ) : (
              <div>
                <CommentQuill
                  value={contents}
                  onChange={setContents}
                  placeholder="내용을 입력해 주세요."
                  minHeight={80}
                  maxHeight={150}
                  noControls
                  id={`miniguestbook-write`}
                  type="editor"
                  setToolbar={{ emoji: true }}
                  collectionPath={mainBoard}
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button
                    onClick={() => setIsWriting(false)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      background: 'transparent',
                      border: '1px solid var(--lineColor)',
                      borderRadius: 'var(--cardBorderRadius)',
                      color: 'var(--textColor)',
                      cursor: 'pointer',
                    }}
                  >
                    취소
                  </button>
                  <button
                    onClick={handleSubmit}
                    style={{
                      flex: 1,
                      padding: '8px',
                      background: 'var(--primaryColor)',
                      border: 'none',
                      borderRadius: 'var(--cardBorderRadius)',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    작성
                  </button>
                </div>
              </div>
            )}
          </WriteSection>
        )}
      </GuestbookGrid>
    </TitleWidgetSec>
  );
}

export default MiniGuestbook;
